import { useState, forwardRef, useEffect, useCallback, useContext } from 'react';
import { Formik, Form, Field } from 'formik';
import { TextField } from 'formik-mui';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    IconButton,
    Slide,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography
} from '@mui/material';
import { Close } from '@mui/icons-material';
import FileSaver from 'file-saver';
// Our Components
import { AuthContext } from 'context/auth-context';
import { GET_CARD_TRANSACTIONS, SHOW_ERROR_ALERT, SHOW_SUCCESS_ALERT } from 'context/transactions/transactionsTypes';
import { TransactionsContext } from 'context/transactions/transactionsContext';
import ChildTransactionsTable from './ChildTransactionsTable';
import ReceiptFileInput from './ReceiptFileInput';
import TransactionsCategoryInput from './TransactionsCategoryInput';
import TransactionsSummaryFooter from './TransactionsSummaryFooter';
// Services
import { Cards } from 'lib/api';
// Utils and Helpers
import { CARD_TRANSACTION_TYPE, USER_ROLES, CARD_TYPE } from 'helpers/constants';
import { numberFormat } from 'helpers/i18n';
// Assets
import { ReactComponent as AlertIcon } from 'assets/AlertIcon.svg';
import { ReactComponent as AttachmentsIcon } from 'assets/AttachmentsIcon.svg';
import { ReactComponent as CalendarIcon } from 'assets/CalendarIcon.svg';
import { ReactComponent as CardCategoryIcon } from 'assets/CardCategoryIcon.svg';
import { ReactComponent as CardIcon } from 'assets/CardIcon.svg';
import { ReactComponent as DownloadReceiptFileIcon } from 'assets/transactionsSection/downloadReceiptIcon.svg';
import { ReactComponent as IdentificationIcon } from 'assets/IdentificationIcon.svg';
import { ReactComponent as MailIcon } from 'assets/MailIcon.svg';
import { ReactComponent as MemoIcon } from 'assets/MemoIcon.svg';
import { ReactComponent as ReceiptFileIcon } from 'assets/transactionsSection/receipt.svg';
import { ReactComponent as StoreIcon } from 'assets/StoreIcon.svg';
import { ReactComponent as TransactionCategoryIcon } from 'assets/transactionsSection/TransactionCategoryIcon.svg';
import { ReactComponent as TransactionIdIcon } from 'assets/transactionsSection/transactionIdIcon.svg';
import './TransactionDetails.css';

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="left" ref={ref} {...props} />;
});

const validationSchema = Yup.object().shape({
    memo: Yup.string().notRequired().max(255, 'Memo must be 255 characters or less').nullable()
});
const TransactionDetails = ({
    isOpen,
    setIsOpen,
    cardTransactionID,
    statusFormatter,
    transactionsCategories,
    transactionsCategoriesRaw
}) => {
    const { userInfo } = useContext(AuthContext);
    const {
        dispatch,
        state: { shouldUpdateCardTransactionList }
    } = useContext(TransactionsContext);
    const [cardTransaction, setCardTransaction] = useState({
        memo: '',
        receipts: []
    });
    const [parentCardTransaction, setParentCardTransaction] = useState({
        memo: '',
        receipts: []
    });
    const [didFormValuesChange, setDidFormValuesChange] = useState(false);
    const [isUnsavedChangesDialogOpen, setIsUnsavedChangesDialogOpen] = useState(false);
    const [isRemoveReceiptDialogOpen, setIsRemoveReceiptDialogOpen] = useState(false);
    const [transactionType, setTransactionType] = useState({
        description: 'amount paid'
    });
    const [receiptToRemoveID, setReceiptToRemoveID] = useState(null);
    const [isChildTransaction, setIsChildTransaction] = useState(false);
    const [transactionId, setTransactionId] = useState(cardTransactionID);
    const [verifier, setVerifier] = useState(false);
    const [childrenTransactions, setChildrenTransactions] = useState([]);
    const [childTransactionsTotalAmount, setChildTransactionsTotalAmount] = useState(0);

    const updateTransactionType = type => {
        let newDescription;

        switch (type) {
            case CARD_TRANSACTION_TYPE.SPEND:
                newDescription = 'amount paid';
                break;
            case CARD_TRANSACTION_TYPE.REFUND:
                newDescription = 'amount refunded';
                break;
            case CARD_TRANSACTION_TYPE.REVERSAL:
                newDescription = 'amount reversed';
                break;
            case CARD_TRANSACTION_TYPE.DECLINED:
                newDescription = 'amount declined';
                break;
            case CARD_TRANSACTION_TYPE.REPLENISH:
                newDescription = 'amount replenished';
                break;
            default:
                return;
        }

        setTransactionType({
            description: newDescription
        });
    };
    const fetchCardTransaction = useCallback(async () => {
        try {
            const cardTransactionData = await Cards.getCardTransaction(transactionId);
            setVerifier(cardTransactionData?.id);

            let childrenTransactionsData = await Cards.getCardTransactionChildren(transactionId);

            if (cardTransactionData?.id_parent_card_transaction) {
                setIsChildTransaction(true);
                const parentCardTransactionData = await Cards.getCardTransaction(cardTransactionData?.id_parent_card_transaction);
                setParentCardTransaction(parentCardTransactionData);

                childrenTransactionsData = await Cards.getCardTransactionChildren(cardTransactionData.id_parent_card_transaction);
                setChildTransactionsTotalAmount(
                    parseFloat(childrenTransactionsData.totalAmount) + parseFloat(parentCardTransactionData.amount)
                );
            }

            setCardTransaction(cardTransactionData);
            setChildrenTransactions({
                ...childrenTransactionsData,
                totalAmount: parseFloat(childrenTransactionsData.totalAmount) + parseFloat(cardTransactionData.amount)
            });
        } catch (error) {
            console.error(error);
        }
    }, [transactionId]);
    useEffect(() => {
        setIsChildTransaction(false);
        fetchCardTransaction();
    }, [fetchCardTransaction, shouldUpdateCardTransactionList]);

    useEffect(() => {
        updateTransactionType(cardTransaction.type);
    }, [cardTransaction]);

    const handleSubmit = async (values, actions) => {
        actions.setSubmitting(true);
        setIsUnsavedChangesDialogOpen(false);
        try {
            await Cards.updateCardTransaction(values.id, { memo: values.memo ? values.memo : null });
            if (values.receipts.length > 0) {
                const body = mapNewReceiptsToBody(values.receipts);
                if (body) {
                    await Cards.uploadCardTransactionReceipts(values.id, body);
                }
            }
            dispatch({ type: GET_CARD_TRANSACTIONS });
            dispatch({ type: SHOW_SUCCESS_ALERT });
        } catch (error) {
            console.error(error);
            dispatch({ type: SHOW_ERROR_ALERT });
        }
        actions.setSubmitting(false);
        setIsOpen(false);
    };
    const mapNewReceiptsToBody = receipts => {
        const body = new FormData();
        const newReceipts = [];
        for (const receipt of receipts) {
            if (receipt.localId) {
                body.append('receipts', receipt);
                newReceipts.push(receipt);
            }
        }
        return newReceipts.length > 0 ? body : null;
    };

    const handleTransactionDetailsClose = async () => {
        if (didFormValuesChange) {
            return setIsUnsavedChangesDialogOpen(true);
        }
        setIsOpen(false);
    };

    const handleUnsavedChangesDialogClose = resetForm => {
        resetForm();
        setIsUnsavedChangesDialogOpen(false);
        setDidFormValuesChange(false);
        setIsOpen(false);
    };

    const handleDownloadReceiptFile = async receipt => {
        FileSaver.saveAs(receipt.url, receipt.filename);
    };

    const handleRemoveReceiptClick = async (receiptID, values, formikActions) => {
        if (formikActions.isSubmitting) return;
        // Remove receipt from local state
        if (typeof receiptID === 'string') {
            const newReceipts = values.receipts.filter(receipt => receipt.localId !== receiptID);
            formikActions.setFieldValue('receipts', newReceipts);
            setIsRemoveReceiptDialogOpen(false);
            return;
        }
        try {
            formikActions.setSubmitting(true);
            await Cards.removeCardTransactionReceipt(values?.id, receiptID);
            const newReceipts = values.receipts.filter(receipt => receipt.id !== receiptID);
            formikActions.setFieldValue('receipts', newReceipts);
        } catch (error) {
            console.error(error);
        }
        setIsRemoveReceiptDialogOpen(false);
        formikActions.setSubmitting(false);
    };

    const canRemoveReceipt = receipt => {
        // Admins can remove all receipts
        if (userInfo.id_role === USER_ROLES.ADMIN) {
            return true;
        }
        // Card holders can remove receipts they uploaded
        if (userInfo.id_role === USER_ROLES.CARD_HOLDER && userInfo.id === receipt?.id_uploader) {
            return true;
        }
        if (userInfo.id_role === USER_ROLES.CARD_HOLDER && receipt.localId) {
            return true;
        }
        return false;
    };

    const isReceiptsUploader = receipts =>
        receipts.every(receipt => userInfo.id === receipt.id_uploader) || receipts.some(receipt => receipt?.localId);

    const calculateFileSizeInMB = fileSize => fileSize / 1024 / 1024;

    const handleRedirect = targetTransactionID => {
        setTransactionId(targetTransactionID);
    };

    return (
        <Dialog
            id="transaction-details"
            fullScreen
            open={isOpen}
            onClose={handleTransactionDetailsClose}
            TransitionComponent={Transition}>
            <DialogContent>
                <Formik
                    initialValues={isChildTransaction ? parentCardTransaction : cardTransaction}
                    enableReinitialize={true}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}>
                    {({ dirty, isSubmitting, setFieldValue, resetForm, setSubmitting, submitForm, values }) => (
                        <Form id="transaction-details__form">
                            {setDidFormValuesChange(dirty)}
                            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="15px">
                                <Box className="edit-card-funds__modal-title">Transaction Details</Box>

                                <Box alignSelf="start">
                                    <IconButton onClick={handleTransactionDetailsClose}>
                                        <Close sx={{ fontSize: '20px', cursor: 'pointer' }} />
                                    </IconButton>
                                </Box>
                            </Box>
                            <Box className="transaction-details__banner">
                                <Box marginBottom="5px">{statusFormatter(cardTransaction)}</Box>

                                <Box component="h3" className="transaction-details__transaction-title">
                                    {numberFormat(cardTransaction.amount, 2, 2)}
                                </Box>

                                <Box component="p" className="transaction-details__transaction-description">
                                    {transactionType.description}
                                </Box>
                            </Box>
                            {!isChildTransaction &&
                                childrenTransactions?.results?.length > 0 &&
                                cardTransaction.balance &&
                                cardTransaction?.id === verifier && (
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="flex-start"
                                        marginTop="auto"
                                        width="100%">
                                        <Box width="100%" marginLeft="auto" marginRight="auto">
                                            <ChildTransactionsTable
                                                childrenTransactions={childrenTransactions}
                                                setTransactionId={setTransactionId}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            {isChildTransaction && (
                                <Box display="flex" flexDirection="column" alignItems="flex-start" marginTop="auto" width="100%">
                                    <Box width="100%" marginLeft="auto" marginRight="auto">
                                        <TransactionsSummaryFooter childTransactionsTotalAmount={childTransactionsTotalAmount} />
                                    </Box>
                                </Box>
                            )}
                            <Box display="flex" flex justifyContent="start" alignItems="flex-start" marginBottom="26px">
                                <Table className="transaction-details__table" size="small" sx={{ width: '100%' }}>
                                    <TableBody sx={{ width: '100%' }}>
                                        <TableRow className="transition-details__row" marginBottom="25px">
                                            <TableCell sx={{ borderBottom: 'none' }}>
                                                <Box className="transaction-details__data-label">
                                                    <CalendarIcon />
                                                    <Box marginLeft="11.5px"> Created Date</Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell className="transaction-details__data-value" sx={{ borderBottom: 'none' }}>
                                                {dayjs(cardTransaction?.solid_created_at).format('MM/DD/YYYY')}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow className="transition-details__row" marginBottom="25px">
                                            <TableCell sx={{ borderBottom: 'none' }}>
                                                <Box className="transaction-details__data-label">
                                                    <TransactionIdIcon />
                                                    <Box marginLeft="11.5px"> Transaction Id</Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell className="transaction-details__data-value" sx={{ borderBottom: 'none' }}>
                                                {cardTransaction?.id}
                                            </TableCell>
                                        </TableRow>
                                        {isChildTransaction && (
                                            <TableRow marginBottom="25px">
                                                <TableCell sx={{ borderBottom: 'none' }}>
                                                    <Box className="transaction-details__data-label">
                                                        <TransactionIdIcon />
                                                        <Box marginLeft="11.5px"> Parent Transaction Id</Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell
                                                    className="transaction-details__data-value"
                                                    sx={{
                                                        borderBottom: 'none',
                                                        color: '#4ABDE9'
                                                    }}>
                                                    <a
                                                        onClick={() => handleRedirect(parentCardTransaction?.id)}
                                                        style={{ cursor: 'pointer' }}>
                                                        {parentCardTransaction?.id}
                                                    </a>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        <TableRow marginBottom="25px">
                                            <TableCell sx={{ borderBottom: 'none' }}>
                                                <Box className="transaction-details__data-label">
                                                    <StoreIcon />
                                                    <Box marginLeft="11.5px"> Paid to</Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell className="transaction-details__data-value" sx={{ borderBottom: 'none' }}>
                                                {cardTransaction?.data?.merchant?.merchantName ||
                                                    cardTransaction?.data?.card?.merchant?.merchantName}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow className="transition-details__row" marginBottom="25px">
                                            <TableCell sx={{ borderBottom: 'none' }}>
                                                <Box className="transaction-details__data-label">
                                                    <IdentificationIcon />
                                                    <Box marginLeft="11.5px"> Paid by</Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell className="transaction-details__data-value" sx={{ borderBottom: 'none' }}>
                                                {cardTransaction?.card?.name ?? 'N/A'}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow className="transition-details__row" marginBottom="25px">
                                            <TableCell sx={{ borderBottom: 'none' }}>
                                                <Box className="transaction-details__data-label">
                                                    <MailIcon />
                                                    <Box marginLeft="11.5px"> Email Address</Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell className="transaction-details__data-value" sx={{ borderBottom: 'none' }}>
                                                {cardTransaction?.card?.email}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow className="transition-details__row" marginBottom="25px">
                                            <TableCell sx={{ borderBottom: 'none' }}>
                                                <Box className="transaction-details__data-label">
                                                    <TransactionCategoryIcon />
                                                    <Box marginLeft="11.5px"> Transaction Category</Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell className="transaction-details__data-value" sx={{ borderBottom: 'none' }}>
                                                <TransactionsCategoryInput
                                                    variant="outlined"
                                                    name="label"
                                                    size="small"
                                                    transaction={values}
                                                    transactionsCategories={transactionsCategories}
                                                    transactionsCategoriesRaw={transactionsCategoriesRaw}
                                                    parentComponentName="TransactionDetails"
                                                />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow className="transition-details__row" marginBottom="25px">
                                            <TableCell sx={{ borderBottom: 'none' }}>
                                                <Box className="transaction-details__data-label">
                                                    <CardCategoryIcon />
                                                    <Box marginLeft="11.5px"> Card Category</Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell className="transaction-details__data-value" sx={{ borderBottom: 'none' }}>
                                                {cardTransaction?.card?.label}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow className="transition-details__row" marginBottom="25px">
                                            <TableCell sx={{ borderBottom: 'none' }}>
                                                <Box className="transaction-details__data-label">
                                                    <CardIcon />
                                                    <Box marginLeft="11.5px"> Payment Method</Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell className="transaction-details__data-value" sx={{ borderBottom: 'none' }}>
                                                {cardTransaction?.card?.type === CARD_TYPE.PHYSICAL ? 'Physical' : 'Virtual'} Defi
                                                Card (*
                                                {cardTransaction?.card?.last4})
                                            </TableCell>
                                        </TableRow>
                                        <TableRow className="transition-details__row" marginBottom="25px">
                                            <TableCell
                                                sx={{
                                                    borderBottom: 'none',
                                                    color: '#617380',
                                                    display: 'flex',
                                                    justifyContent: 'start',
                                                    alignItems: 'flex-start'
                                                }}>
                                                <Box className="transaction-details__data-label">
                                                    <MemoIcon />
                                                    <Box marginLeft="11.5px">Transaction Memo</Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: 'none', color: '#182C55' }}>
                                                <FormControl fullWidth>
                                                    <Field
                                                        component={TextField}
                                                        name="memo"
                                                        multiline
                                                        rows={3}
                                                        size="small"
                                                        placeholder="Add a transaction memo"
                                                    />
                                                </FormControl>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow className="transition-details__row" marginBottom="25px">
                                            <TableCell
                                                sx={{
                                                    borderBottom: 'none',
                                                    color: '#617380',
                                                    display: 'flex',
                                                    justifyContent: 'start',
                                                    alignItems: 'flex-start'
                                                }}>
                                                <Box className="transaction-details__data-label">
                                                    <AttachmentsIcon />
                                                    <Box marginLeft="11.5px"> Attachments</Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: 'none' }}>
                                                <ReceiptFileInput />
                                            </TableCell>
                                        </TableRow>
                                        {values.receipts.length > 0 && (
                                            <TableRow className="transition-details__row" marginBottom="25px">
                                                <TableCell
                                                    className="transition-details__placeholder"
                                                    sx={{ borderBottom: 'none' }}></TableCell>
                                                <TableCell sx={{ borderBottom: 'none' }}>
                                                    {values.receipts.map(receipt => (
                                                        <Box
                                                            key={receipt?.id || receipt?.localId}
                                                            component="label"
                                                            className="transaction-details__receipt-file"
                                                            sx={{ marginBottom: '8px' }}>
                                                            <Box display="flex" alignItems="center" padding="7px 0px 7px 16px">
                                                                <Box
                                                                    component="span"
                                                                    className="transaction-details__receipt-file-icon-container"
                                                                    marginRight="10px">
                                                                    <ReceiptFileIcon />
                                                                </Box>
                                                                <Box
                                                                    component="span"
                                                                    className="transaction-details__receipt-file-name">
                                                                    {receipt?.name || receipt?.filename}
                                                                </Box>

                                                                {receipt?.size && (
                                                                    <Box component="span" marginLeft="5px">
                                                                        {`${calculateFileSizeInMB(receipt?.size).toFixed(2)} MB`}
                                                                    </Box>
                                                                )}
                                                            </Box>

                                                            <Box className="transaction-details__receipt-actions">
                                                                {receipt && receipt?.id && (
                                                                    <Box
                                                                        className="transaction-details__receipt-actions-download-container"
                                                                        onClick={() => handleDownloadReceiptFile(receipt)}>
                                                                        <DownloadReceiptFileIcon className="transaction-details__receipt-actions-download" />
                                                                    </Box>
                                                                )}
                                                                {canRemoveReceipt(receipt) && (
                                                                    <Box
                                                                        className="transaction-details__receipt-actions-remove-container"
                                                                        onClick={() => {
                                                                            setIsRemoveReceiptDialogOpen(true);
                                                                            setReceiptToRemoveID(receipt?.id || receipt?.localId);
                                                                        }}>
                                                                        <Close sx={{ fontSize: '20px' }} />
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                    {!isReceiptsUploader(values.receipts) &&
                                                        userInfo.id_role === USER_ROLES.CARD_HOLDER && (
                                                            <Alert severity="info" sx={{ marginTop: '10px' }}>
                                                                Receipts uploaded by an admin can’t be deleted. Contact your
                                                                admin.
                                                            </Alert>
                                                        )}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Box>

                            <Box display="flex" justifyContent="end" alignItems="flex-end" marginTop="auto">
                                <Button variant="contained" type="submit" disabled={isSubmitting || !dirty}>
                                    {isSubmitting ? <CircularProgress size="20px" /> : 'Save'}
                                </Button>
                            </Box>

                            <Dialog open={isUnsavedChangesDialogOpen} onClose={() => {}}>
                                <DialogContent sx={{ padding: '32px' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                                        <AlertIcon />
                                        <Box sx={{ display: 'inline-block', marginLeft: '20px' }}>
                                            <Typography className="form-title">You have unsaved changes.</Typography>
                                            <Typography className="text-secondary" sx={{ color: 'red' }}>
                                                You have made some changes. Do you want to save them?
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <DialogActions sx={{ marginTop: '32px', padding: '0px' }}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => handleUnsavedChangesDialogClose(resetForm)}
                                            disabled={isSubmitting}
                                            sx={{ color: '#7D95A5', borderColor: '#7D95A5' }}>
                                            Discard
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={isSubmitting}
                                            onClick={submitForm}
                                            sx={{
                                                backgroundColor: '#FF9E43',
                                                borderColor: '#7D95A5',
                                                '&:hover': {
                                                    backgroundColor: '#e66f00'
                                                }
                                            }}>
                                            {isSubmitting ? <CircularProgress size="20px" /> : 'Save'}
                                        </Button>
                                    </DialogActions>
                                </DialogContent>
                            </Dialog>

                            <Dialog
                                open={isRemoveReceiptDialogOpen}
                                onClose={e => {
                                    setIsRemoveReceiptDialogOpen(false);
                                }}
                                maxWidth="xs">
                                <DialogContent className="transaction-details__remove-receipt" sx={{ padding: '32px' }}>
                                    <Typography className="form-title">Are you sure?</Typography>
                                    <Typography className="text-secondary">You won’t be able to reverse it.</Typography>
                                    <DialogActions sx={{ marginTop: '32px', padding: '0px' }}>
                                        <Button
                                            color="error"
                                            variant="text"
                                            onClick={() => setIsRemoveReceiptDialogOpen(false)}
                                            disabled={isSubmitting}>
                                            Close
                                        </Button>

                                        <Button
                                            color="error"
                                            variant="contained"
                                            onClick={() =>
                                                handleRemoveReceiptClick(receiptToRemoveID, values, {
                                                    isSubmitting,
                                                    setSubmitting,
                                                    setFieldValue
                                                })
                                            }
                                            disabled={isSubmitting}>
                                            {isSubmitting ? <CircularProgress size="20px" /> : 'Remove'}
                                        </Button>
                                    </DialogActions>
                                </DialogContent>
                            </Dialog>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    );
};

export default TransactionDetails;
